import React, {
  type Dispatch,
  forwardRef,
  type SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Platform, type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import Animated, { FadeInDown, FadeOut } from "react-native-reanimated";

import { palette } from "../styles";
import Block from "./Block";
import SvgIcon from "./SvgIcon";
import Typography from "./Typography";

type Props = {
  text: string;
  type?: "success" | "error";
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  containerStyles?: StyleProp<ViewStyle>;
};

const SNACKBAR_VISIBLE_DURATION = 1600;

const Snackbar = forwardRef(
  ({ type = "success", text, isVisible, setIsVisible, containerStyles }: Props, ref) => {
    const [canBeDismissed, setCanBeDismissed] = useState(type === "success");

    useImperativeHandle(ref, () => {
      return {
        hide: () => {
          if (canBeDismissed) {
            setIsVisible(false);
            setCanBeDismissed(false);
          }
        },
      };
    }, [canBeDismissed, setIsVisible]);

    const icon = type === "success" ? "done" : "cross";
    const iconColor = type === "success" ? palette.primary.pinkSalmon : palette.common.white;
    const textType = type === "success" ? "lightError" : "white";
    const containerBg = type === "success" ? palette.primary.accent : palette.secondary.wrongPink;

    useEffect(() => {
      if (isVisible) {
        const timer = setTimeout(() => {
          if (type === "success") {
            setIsVisible(false);
          } else {
            setCanBeDismissed(true);
          }
        }, SNACKBAR_VISIBLE_DURATION);

        return () => clearTimeout(timer);
      }

      return;
    }, [isVisible, type, setIsVisible]);

    if (!isVisible) return null;

    return (
      <Animated.View
        style={[styles.container, containerStyles]}
        entering={FadeInDown}
        exiting={FadeOut}
      >
        <Block
          alignCenter
          row
          onTouchEnd={() => setIsVisible(false)}
          style={[styles.content, { backgroundColor: containerBg }]}
        >
          <SvgIcon color={iconColor} name={icon} size={22} />
          <Block style={styles.text}>
            <Typography type={textType} variant="bodyBold">
              {text}
            </Typography>
          </Block>
        </Block>
      </Animated.View>
    );
  },
);

export default React.memo(Snackbar);

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
    bottom: 0,
    left: 0,
    overflow: "hidden",
    ...Platform.select({
      default: {
        position: "relative",
      },
      web: {
        // biome-ignore lint/suspicious/noExplicitAny: web styles
        position: "sticky" as any,
      },
    }),
    right: 0,
    zIndex: 2,
  },
  content: {
    paddingHorizontal: 21,
    paddingVertical: 16,
  },
  text: {
    paddingLeft: 13,
    paddingRight: 16,
  },
});
